import type { InvestorProfile } from '@/api/custom';
import { normalizeInvestorLifecycleStatus } from '@/common/investor-lifecycle-status';

export const ALL_CLIENT_LIFECYCLE_FILTER = 'all';

export const CLIENT_LIFECYCLE_STAGE_FILTERS = [
  'New Prospect',
  'Discovery',
  'Proposal',
  'Onboarding',
  'Active Client',
  'Dormant',
] as const;

export type ClientLifecycleStage = (typeof CLIENT_LIFECYCLE_STAGE_FILTERS)[number];
export type ClientLifecycleFilter = typeof ALL_CLIENT_LIFECYCLE_FILTER | ClientLifecycleStage;

export type ClientRosterRow = {
  investor: InvestorProfile;
  displayName: string;
  lifecycleStatus: string;
  stage: ClientLifecycleStage;
  contactLine: string;
  searchText: string;
};

export const deriveClientLifecycleStage = (value?: string | null): ClientLifecycleStage => {
  const status = normalizeInvestorLifecycleStatus(value);

  switch (status) {
    case 'Discovery Scheduled':
      return 'Discovery';
    case 'Proposal in Progress':
    case 'Prospect Reviewing Proposal':
      return 'Proposal';
    case 'Onboarding Client':
      return 'Onboarding';
    case 'Active Client':
      return 'Active Client';
    case 'Unresponsive':
    case 'Not a Fit':
    case 'Lost / Declined':
      return 'Dormant';
    case 'New Prospect':
    case 'First Contact Made':
    case 'Qualified Prospect':
    default:
      return 'New Prospect';
  }
};

export const buildClientContactLine = (investor: InvestorProfile, lifecycleStatus: string) => {
  const primaryContact =
    investor.email?.trim() || investor.phone_number?.trim() || 'No contact on file';
  return `${lifecycleStatus} - ${primaryContact}`;
};

export const buildClientRosterRows = (
  investors: InvestorProfile[],
  getDisplayName: (investor: Pick<InvestorProfile, 'name'>) => string,
): ClientRosterRow[] =>
  investors
    .map((investor) => {
      const displayName = getDisplayName(investor);
      const lifecycleStatus = normalizeInvestorLifecycleStatus(investor.lifecycle_status);
      const stage = deriveClientLifecycleStage(lifecycleStatus);
      const contactLine = buildClientContactLine(investor, lifecycleStatus);
      const searchText = [
        displayName,
        lifecycleStatus,
        stage,
        investor.email,
        investor.phone_number,
        contactLine,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return {
        investor,
        displayName,
        lifecycleStatus,
        stage,
        contactLine,
        searchText,
      };
    })
    .sort((left, right) => {
      const nameComparison = left.displayName.localeCompare(right.displayName);
      return nameComparison === 0
        ? left.investor.id.localeCompare(right.investor.id)
        : nameComparison;
    });

export const filterClientRosterRows = (
  rows: ClientRosterRow[],
  searchPhrase: string,
  lifecycleFilter: ClientLifecycleFilter,
): ClientRosterRow[] => {
  const normalizedSearch = searchPhrase.trim().toLowerCase();

  return rows.filter((row) => {
    if (lifecycleFilter !== ALL_CLIENT_LIFECYCLE_FILTER && row.stage !== lifecycleFilter) {
      return false;
    }

    return normalizedSearch ? row.searchText.includes(normalizedSearch) : true;
  });
};

export const formatCompactCurrency = (value: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: value >= 1_000_000 ? 'compact' : 'standard',
    minimumFractionDigits: 0,
    maximumFractionDigits: value >= 1_000_000 ? 1 : 0,
  }).format(value);

export const csvCell = (value: string | number | null | undefined): string => {
  const text = value == null ? '' : String(value);
  const guarded = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  return `"${guarded.replace(/"/g, '""')}"`;
};
